import React, { useState, useRef, useEffect } from 'react';
import '../styles/DatePicker.css';

interface DatePickerProps {
  value: string;
  onChange: (date: string) => void;
  placeholder?: string;
  label?: string;
}

const DatePicker: React.FC<DatePickerProps> = ({ value, onChange, placeholder, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const pickerRef = useRef<HTMLDivElement>(null);

  const formatDate = (date: string) => {
    if (!date) return placeholder || 'Select date';
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const current = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };

  const handleDateClick = (date: Date) => {
    const dateString = date.toISOString().split('T')[0];
    onChange(dateString);
    setIsOpen(false);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const days = getDaysInMonth(currentMonth);
  const monthYear = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const today = new Date().toISOString().split('T')[0];
  const selectedDate = value ? new Date(value) : null;

  return (
    <div className="date-picker" ref={pickerRef}>
      {label && <label className="date-picker-label">{label}</label>}
      <div 
        className={`date-picker-input ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={`date-display ${value ? 'selected' : ''}`}>
          {formatDate(value)}
        </span>
        <i className="fas fa-calendar-alt date-icon"></i>
      </div>

      {isOpen && (
        <div className="calendar-popup">
          <div className="calendar-header">
            <button 
              type="button"
              className="nav-btn"
              onClick={handlePrevMonth}
            >
              <i className="fas fa-chevron-left"></i>
            </button>
            <h4 className="month-year">{monthYear}</h4>
            <button 
              type="button"
              className="nav-btn"
              onClick={handleNextMonth}
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>

          <div className="calendar-grid">
            <div className="weekdays">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="weekday">{day}</div>
              ))}
            </div>
            
            <div className="days-grid">
              {days.map((date, index) => {
                const dateString = date.toISOString().split('T')[0];
                const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
                const isToday = dateString === today;
                const isSelected = selectedDate && dateString === value;
                const isPast = date < new Date(today);

                return (
                  <button
                    key={index}
                    type="button"
                    className={`day-cell ${isCurrentMonth ? 'current-month' : 'other-month'} ${
                      isToday ? 'today' : ''
                    } ${isSelected ? 'selected' : ''} ${isPast ? 'past' : ''}`}
                    onClick={() => handleDateClick(date)}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="calendar-footer">
            <button 
              type="button"
              className="clear-btn"
              onClick={() => {
                onChange('');
                setIsOpen(false);
              }}
            >
              Clear
            </button>
            <button 
              type="button"
              className="today-btn"
              onClick={() => {
                onChange(today);
                setIsOpen(false);
              }}
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;